import { useEffect, useState } from 'react';
import { API_BASE_URL, authFetch } from '@/services/api';
import {
  OwnerStation,
  OwnerIncident,
  OwnerLostRevenue,
  stations as mockStations,
  incidents as mockIncidents,
} from '@/mocks/ownerDashboard';

export interface OwnerKpis {
  earnedMonthUah: number;
  earnedTodayUah: number;
  forecastTodayRevenueUah: number;
  forecastTodayMwh: number;
  factNowMw: number;
  workingCount: number;
  totalCount: number;
  preLaunchCount: number;
}

interface OwnerDashboardResponse {
  stations: OwnerStation[];
  incidents: OwnerIncident[];
  kpis: OwnerKpis | null;
  lostRevenue: OwnerLostRevenue | null;
  loading: boolean;
  /** true when live data failed and mock fallback is being shown */
  isFallback: boolean;
}

/**
 * Live owner-dashboard data from GET /owner/dashboard.
 * Replaces the static Readdy mock — real per-station status (incl. pre-launch),
 * measured generation, day-ahead guaranteed revenue and portfolio KPIs.
 * Falls back to the mock arrays (clearly marked) if the API is unreachable.
 */
export function useOwnerDashboard(): OwnerDashboardResponse {
  const [stations, setStations] = useState<OwnerStation[]>([]);
  const [incidents, setIncidents] = useState<OwnerIncident[]>([]);
  const [kpis, setKpis] = useState<OwnerKpis | null>(null);
  const [lostRevenue, setLostRevenue] = useState<OwnerLostRevenue | null>(null);
  const [loading, setLoading] = useState(true);
  const [isFallback, setIsFallback] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await authFetch(`${API_BASE_URL}/owner/dashboard`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const d = await res.json();
        if (!alive) return;
        setStations(d.stations ?? []);
        setIncidents(d.incidents ?? []);
        setKpis(d.kpis ?? null);
        setLostRevenue(d.lostRevenue ?? null);
        setIsFallback(false);
      } catch {
        if (!alive) return;
        setStations(mockStations);
        setIncidents(mockIncidents);
        setKpis(null);
        setLostRevenue(null);
        setIsFallback(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  return { stations, incidents, kpis, lostRevenue, loading, isFallback };
}
